import useStoreLocatorStyles from './StoreLocator.styles';
import { useEffect, useState } from 'react';
import { Skeleton, SkeletonItem, useId } from '@fluentui/react-components';
import StoreLocatorApi from './StoreLocator.api';
import { IStoreLocation, IStoreLocatorProps } from './StoreLocator.types';
import StoreLocatorResultRadioGroup from './StoreLocatorResultRadioGroup';
import CheckboxField from '../inputs/InputTypes/CheckboxField';
import SecondaryButton from '../../core/ViaeroButton/SecondaryButton/SecondaryButton';
import { useCart } from '../../../contexts/CartContext/CartContext.hooks';
import BaseInputField from '../inputs/BaseInputField/BaseInputField';
import NoSearchResults from './NoSearchResults';

const StoreLocator = (props: IStoreLocatorProps) => {
    const { searchType, buttonText, shippingCost } = props;

    const styles = useStoreLocatorStyles();

    const { cart } = useCart();

    const defaultSearch =
        `${cart.customerInfo.address.addressLine1}, ${cart.customerInfo.address.city}, ${cart.customerInfo.address.state} ${cart.customerInfo.address.zipcode}`.trim();

    const [search, setSearch] = useState(defaultSearch);

    const defaultResultSet: IStoreLocation[] = [];

    const [results, setResults] = useState(defaultResultSet);

    const [searching, setSearching] = useState(false);

    const [searchError, setSearchError] = useState(false);

    useEffect(() => {
        const sanitized = handleSanitizeSearch(search);
        if (sanitized !== '') {
            handleSearch(sanitized);
        }
    }, []);

    const handleSearch = (searchValue?: string) => {
        setSearchError(false);
        if (searchValue) {
            doSearch(searchValue);
        } else {
            const sanitized = handleSanitizeSearch(search);
            doSearch(sanitized);
        }
    };

    const doSearch = (searchValue: string) => {
        setSearching(true);
        setResults([]);

        switch (searchType) {
            case 'store':
                StoreLocatorApi.SearchStores(searchValue, handleResultsFound, handleSearchFailure);
                break;
            case 'post-office':
                StoreLocatorApi.FindPostOffices(
                    searchValue,
                    handleResultsFound,
                    handleSearchFailure
                );
                break;
        }
    };

    const handleResultsFound = (searchResults: IStoreLocation[]) => {
        setResults(searchResults);
        setSearching(false);
    };

    const handleSearchFailure = () => {
        setSearchError(true);
    };

    const handleSanitizeSearch = (searchString: string): string => {
        return searchString.trim();
    };

    const radioGroupName = useId('resultGroup');

    return (
        <div className={styles.root}>
            <div className={styles.contentWrapper}>
                <div className={styles.inputWrapper}>
                    <BaseInputField
                        value={search}
                        data-testid={'store-locator-input'}
                        placeholder={'Enter address, zip code'}
                        name={'store-locator'}
                        label={'Search by location'}
                        onChange={setSearch}
                    />
                </div>

                <div className={styles.buttonWrapper}>
                    <SecondaryButton
                        click={() => handleSearch()}
                        text={buttonText}
                        testId={'find-store-button'}
                        disabled={search.trim() === '' || search.trim().length < 5}
                    />
                </div>
            </div>

            {searching && !searchError && (
                <Skeleton className={styles.loadingSkeleton}>
                    <SkeletonItem size={16} />
                    <SkeletonItem size={16} />
                    <SkeletonItem size={16} />
                </Skeleton>
            )}

            {results.length > 0 && (
                <div className={styles.searchResultsWrapper} data-testid={'search-results'}>
                    <div className={styles.searchResults}>
                        <StoreLocatorResultRadioGroup
                            results={results}
                            groupName={radioGroupName}
                        />
                    </div>

                    <div>
                        <div className={styles.overnightShipping}>Need it faster?</div>

                        <div className={styles.overnightCheckbox}>
                            <CheckboxField
                                checked={props.overnightShipping}
                                label={`Check for overnight shipping`}
                                onChange={(e) => props.setOvernightShipping(e)}
                            />
                            <div className={styles.methodCost}>{`+$${(searchType === 'store'
                                ? shippingCost.overnightStore
                                : shippingCost.overnightPostOffice
                            ).toFixed(2)}`}</div>
                        </div>
                    </div>
                </div>
            )}

            {searchError && <NoSearchResults />}
        </div>
    );
};

export default StoreLocator;
